import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { MessageSquare, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export const FeedbackIconButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackType, setFeedbackType] = useState('feature');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);
      const response = await fetch('https://formspree.io/f/meogbzvg', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Vielen Dank für Ihr Feedback!');
        setIsOpen(false);
        (e.target as HTMLFormElement).reset();
      } else {
        throw new Error('Fehler beim Senden des Feedbacks');
      }
    } catch (error) {
      toast.error('Fehler beim Senden des Feedbacks');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="p-2 hover:bg-gray-100"
        title="Feedback geben"
      >
        <MessageSquare className="w-4 h-4" />
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">Feedback & Wünsche</DialogTitle>
            <DialogDescription className="text-center text-gray-500">
              Teilen Sie uns Ihre Ideen und Verbesserungsvorschläge mit
            </DialogDescription>
          </DialogHeader>
          <motion.form 
            onSubmit={handleSubmit} 
            className="space-y-6 mt-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="space-y-2">
              <Label htmlFor="type" className="text-sm font-medium">Art des Feedbacks</Label>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  variant={feedbackType === 'feature' ? 'default' : 'outline'}
                  className="w-full"
                  onClick={() => setFeedbackType('feature')}
                >
                  Funktion
                </Button>
                <Button
                  type="button"
                  variant={feedbackType === 'bug' ? 'default' : 'outline'}
                  className="w-full"
                  onClick={() => setFeedbackType('bug')}
                >
                  Bug
                </Button>
                <Button
                  type="button"
                  variant={feedbackType === 'other' ? 'default' : 'outline'}
                  className="w-full"
                  onClick={() => setFeedbackType('other')}
                >
                  Sonstiges
                </Button>
              </div>
              <input type="hidden" name="type" value={feedbackType} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-sm font-medium">Ihre Nachricht</Label>
              <Textarea
                id="message"
                name="message"
                placeholder={
                  feedbackType === 'feature' 
                    ? "Beschreiben Sie Ihren Funktionswunsch..."
                    : feedbackType === 'bug'
                      ? "Beschreiben Sie das Problem..."
                      : "Ihre Nachricht..."
                }
                className="min-h-[120px] resize-none"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                E-Mail für Rückfragen <span className="text-gray-400">(optional)</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="ihre@email.de"
                className="bg-gray-50"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={isSubmitting} className="min-w-[100px]">
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Senden
                  </>
                )}
              </Button>
            </div>
          </motion.form>
        </DialogContent>
      </Dialog>
    </>
  );
}; 